"use client";

import { track } from "@vercel/analytics";
import { useRef, useState, type ReactNode } from "react";

import type { SiteVideo } from "@/lib/site-videos";

const BRIEFING = [
  {
    title: "How the work happens now",
    detail: "Where quotes, enquiries and follow-ups depend on somebody remembering.",
  },
  {
    title: "What Ownly builds",
    detail: "Websites, apps and internal systems, explained through the work they do.",
  },
  {
    title: "Work you can inspect",
    detail: "Client work, our own product and working examples, each labelled for what it is.",
  },
  {
    title: "What happens if you get in touch",
    detail: "The review, the written scope, the build, the launch and what comes after.",
  },
] as const;

const SPEEDS = [1, 1.25, 1.5, 2] as const;

/**
 * One founder film slot, used by /before-we-talk and /booked.
 *
 * `fallback` is what the page shows while there is no recording. /before-we-talk keeps its
 * own briefing placeholder (the default below); /booked passes real content instead, because
 * someone who has just booked should get something useful rather than a promise of a film.
 * `eventName` keeps each page's analytics separate: "<eventName>-started", "-progress",
 * "-completed".
 */
export function DecisionVideo({
  video,
  fallback,
  eventName = "decision-video",
}: {
  video: SiteVideo | null;
  fallback?: ReactNode;
  eventName?: string;
}) {
  const player = useRef<HTMLVideoElement>(null);
  const sentProgress = useRef(new Set<number>());
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<number>(1);

  if (!video && fallback) return <>{fallback}</>;

  if (!video) {
    return (
      <div className="relative overflow-hidden rounded-[1.75rem] border border-white/[0.14] bg-[#080e19] shadow-[0_35px_90px_-45px_rgba(0,0,0,0.9)]">
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_72%_18%,rgba(67,141,255,0.2),transparent_34%),linear-gradient(145deg,rgba(255,255,255,0.035),transparent_45%)]"
          aria-hidden
        />
        <div className="relative grid lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
          <div className="flex flex-col justify-between border-b border-white/[0.1] p-6 sm:p-8 lg:border-b-0 lg:border-r lg:p-9">
            <div>
              <p className="ownly-display max-w-sm text-[1.8rem] leading-[35px] text-white sm:text-[2.2rem] sm:leading-[42px]">
                The Ownly briefing will play here.
              </p>
            </div>
            <p className="mt-6 max-w-sm text-copy leading-[24px] text-white/62">
              The recording is being prepared. Everything it covers is on this page in the
              meantime.
            </p>
          </div>

          <ol className="relative flex flex-col justify-center p-6 sm:p-8 lg:p-9">
            {BRIEFING.map((item) => (
              <li
                key={item.title}
                className="border-t border-white/[0.1] py-4 first:border-t-0"
              >
                <span>
                  <span className="block text-body font-semibold tracking-tight text-white">
                    {item.title}
                  </span>
                  <span className="mt-1.5 block text-copy leading-[23px] text-white/58">
                    {item.detail}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    );
  }

  async function start() {
    const element = player.current;
    if (!element) return;
    element.muted = false;
    element.controls = true;
    element.playbackRate = speed;
    setPlaying(true);
    track(`${eventName}-started`);
    try {
      await element.play();
    } catch {
      // A browser can refuse or interrupt a play request. Safari rejects with AbortError
      // when the visitor pauses, or clicks the frame, before playback has actually begun.
      // Left alone that is an unhandled rejection, and the overlay has already gone, so
      // they are looking at a still frame with no play button. Put the button back unless
      // the video really is running, which happens when a second press supersedes the first.
      setPlaying(!element.paused);
    }
  }

  function recordProgress() {
    const element = player.current;
    if (!element || !Number.isFinite(element.duration) || element.duration <= 0) return;
    const percentage = (element.currentTime / element.duration) * 100;
    for (const point of [10, 25, 50, 75, 90]) {
      if (percentage >= point && !sentProgress.current.has(point)) {
        sentProgress.current.add(point);
        track(`${eventName}-progress`, { percentage: point });
      }
    }
  }

  const dimensions = video.dimensions ?? { width: 16, height: 9 };

  return (
    <figure className="m-0">
      <div className="relative overflow-hidden rounded-[1.75rem] border border-white/[0.14] bg-black shadow-[0_35px_90px_-45px_rgba(0,0,0,0.9)]">
        <video
          ref={player}
          className="block w-full bg-black object-contain"
          style={{ aspectRatio: `${dimensions.width} / ${dimensions.height}` }}
          src={video.src}
          poster={video.poster}
          preload="metadata"
          playsInline
          muted
          controlsList="nodownload"
          disablePictureInPicture
          onTimeUpdate={recordProgress}
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
          onEnded={() => {
            setPlaying(false);
            track(`${eventName}-completed`);
          }}
        >
          {video.captions ? (
            <track
              kind="captions"
              srcLang="en"
              label="English"
              src={video.captions}
              default
            />
          ) : null}
        </video>

        {playing ? null : (
          <button
            type="button"
            onClick={start}
            aria-label={`Play the video: ${video.description}`}
            className="group absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/65 via-black/5 to-black/10 transition-colors duration-300 hover:from-black/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ownly-accent"
          >
            <span className="flex size-[4.75rem] items-center justify-center rounded-full bg-ownly-accent-cta text-white shadow-[var(--ownly-cta-shadow)] transition-transform duration-300 group-hover:scale-105 group-active:scale-95">
              <svg viewBox="0 0 24 24" className="ml-1 size-7" fill="currentColor" aria-hidden>
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
            {video.length ? (
              <span className="absolute bottom-4 right-4 rounded-full border border-white/15 bg-black/65 px-3 py-1 text-sm font-medium text-white/90 backdrop-blur-sm">
                {video.length}
              </span>
            ) : null}
          </button>
        )}
      </div>
      <figcaption className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-8">
        <span className="max-w-3xl text-copy leading-[24px] text-white/62">{video.description}</span>
        {/* Speed, for people who want the ten minutes in seven. Native controls offer it on some
            browsers and hide it on others, so it is offered here for everyone. */}
        <span role="group" aria-label="Playback speed" className="flex shrink-0 items-center gap-1.5">
          <span className="mr-1 text-sm text-white/50">Speed</span>
          {SPEEDS.map((rate) => (
            <button
              key={rate}
              type="button"
              aria-pressed={speed === rate}
              onClick={() => {
                setSpeed(rate);
                if (player.current) player.current.playbackRate = rate;
                track(`${eventName}-speed`, { rate: String(rate) });
              }}
              data-consistency-ok
              className={`min-h-[36px] min-w-[44px] rounded-full border px-2.5 text-sm tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent ${
                speed === rate
                  ? "border-ownly-accent/60 bg-ownly-accent/15 text-white"
                  : "border-white/[0.14] text-white/65 hover:border-white/30 hover:text-white"
              }`}
            >
              {rate}x
            </button>
          ))}
        </span>
      </figcaption>
      {video.transcript?.length ? (
        <details className="group mt-5 max-w-3xl border-t border-white/[0.12] pt-4">
          <summary
            onClick={() => track(`${eventName}-transcript`)}
            className="flex cursor-pointer list-none items-center justify-between gap-5 text-body font-semibold text-white marker:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent/60 [&::-webkit-details-marker]:hidden"
          >
            Read the transcript
            <span
              className="flex size-8 shrink-0 items-center justify-center rounded-full border border-white/[0.14] text-ownly-accent-text transition-transform duration-200 group-open:rotate-45 motion-reduce:transition-none"
              aria-hidden
            >
              +
            </span>
          </summary>
          <div className="mt-4 grid gap-4 text-body leading-[27px] text-white/72">
            {video.transcript.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </details>
      ) : null}
    </figure>
  );
}
